import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { ShoppingBag, Menu, X, Instagram, Facebook, User as UserIcon } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useStore } from "@/context/StoreContext";
import { WhatsAppIcon } from "@/components/Footer";

const NAV = [
  { to: "/shop", label: "Shop", id: "nav-shop" },
  { to: "/about", label: "About", id: "nav-about" },
  { to: "/contact", label: "Contact", id: "nav-contact" },
  { to: "/track-order", label: "Track Order", id: "nav-track" },
];

export default function Header() {
  const { cartCount, setDrawerOpen, currency, setCurrency, config, user } = useStore();
  const [menuOpen, setMenuOpen] = useState(false);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const signIn = () => {
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + "/account")}`;
  };

  return (
    <header className="fixed inset-x-0 top-0 z-50">
      <div className="bg-crimson py-2 text-center">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white sm:text-[11px]">
          Free shipping in India above ₹2,999
        </p>
      </div>
      <div className="border-b border-linec/80 bg-white/85 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:h-[72px] lg:px-8">
          <button
            data-testid="header-mobile-menu-button"
            className="p-2 lg:hidden"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Menu"
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>

          <Link to="/" data-testid="header-logo-link" className="flex items-center">
            <img src="/logo.png" alt="Just Kords" className="h-10 w-auto sm:h-11" />
          </Link>

          <nav className="hidden items-center gap-9 lg:flex">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={n.id}
                className={({ isActive }) =>
                  `text-[13px] font-semibold uppercase tracking-[0.18em] transition-colors duration-200 hover:text-crimson ${isActive ? "text-crimson" : "text-ink"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3 sm:gap-4">
            {user ? (
              <Link to="/account" data-testid="header-account-link" className="flex items-center gap-2 rounded-full border border-linec py-1 pl-1 pr-3 transition-colors duration-200 hover:border-crimson">
                {user.picture
                  ? <img src={user.picture} alt="" className="h-7 w-7 rounded-full object-cover" />
                  : <span className="flex h-7 w-7 items-center justify-center rounded-full bg-crimson text-xs font-bold text-white">{(user.name || user.email)[0].toUpperCase()}</span>}
                <span className="hidden text-xs font-semibold sm:block">{(user.name || user.email).split(" ")[0]}</span>
              </Link>
            ) : (
              <button data-testid="header-signin" onClick={signIn} className="hidden items-center gap-1.5 rounded-full bg-ink px-4 py-2 text-xs font-semibold text-white transition-colors duration-200 hover:bg-crimson md:flex">
                <UserIcon size={14} /> Sign in
              </button>
            )}
            <div className="hidden items-center gap-0.5 md:flex" data-testid="header-socials">
              <a
                data-testid="header-instagram-link"
                href={(config.socials || {}).instagram || "https://instagram.com/justkords"}
                target="_blank" rel="noopener noreferrer"
                aria-label="Instagram"
                className="rounded-full p-2 text-ink transition-colors duration-200 hover:bg-crimson-sub hover:text-crimson"
              >
                <Instagram size={17} />
              </a>
              <a
                data-testid="header-facebook-link"
                href={(config.socials || {}).facebook || "https://facebook.com/justkords"}
                target="_blank" rel="noopener noreferrer"
                aria-label="Facebook"
                className="rounded-full p-2 text-ink transition-colors duration-200 hover:bg-crimson-sub hover:text-crimson"
              >
                <Facebook size={17} />
              </a>
              <a
                data-testid="header-whatsapp-link"
                href={(config.socials || {}).whatsapp || "https://wa.me/919960705886"}
                target="_blank" rel="noopener noreferrer"
                aria-label="WhatsApp"
                className="rounded-full p-2 text-ink transition-colors duration-200 hover:bg-crimson-sub hover:text-crimson"
              >
                <WhatsAppIcon size={16} />
              </a>
            </div>
            <div
              data-testid="header-currency-toggle"
              className="hidden items-center rounded-full border border-linec p-0.5 sm:flex"
            >
              {["INR", "USD"].map((c) => (
                <button
                  key={c}
                  data-testid={`header-currency-${c.toLowerCase()}`}
                  onClick={() => setCurrency(c)}
                  className={`rounded-full px-3 py-1 font-mono text-[11px] tracking-wider transition-colors duration-200 ${
                    currency === c ? "bg-crimson text-white" : "text-mutedc hover:text-ink"
                  }`}
                >
                  {c === "INR" ? "₹ INR" : "$ USD"}
                </button>
              ))}
            </div>
            <button
              data-testid="header-cart-button"
              onClick={() => setDrawerOpen(true)}
              className="relative rounded-full p-2 transition-colors duration-200 hover:bg-crimson-sub"
              aria-label="Cart"
            >
              <ShoppingBag size={21} strokeWidth={1.75} />
              {cartCount > 0 && (
                <span
                  data-testid="header-cart-count"
                  className="absolute -right-0.5 -top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-crimson font-mono text-[10px] font-medium text-white"
                >
                  {cartCount}
                </span>
              )}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {menuOpen && (
            <motion.nav
              data-testid="header-mobile-menu"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="overflow-hidden border-t border-linec/70 bg-white/95 backdrop-blur-xl lg:hidden"
            >
              <div className="space-y-1 px-6 py-4">
                {NAV.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    data-testid={`${n.id}-mobile`}
                    onClick={() => setMenuOpen(false)}
                    className="block py-2.5 font-serif text-2xl text-ink"
                  >
                    {n.label}
                  </NavLink>
                ))}
                <div className="flex items-center gap-2 pt-3">
                  {["INR", "USD"].map((c) => (
                    <button
                      key={c}
                      onClick={() => setCurrency(c)}
                      className={`rounded-full border px-4 py-1.5 font-mono text-xs ${currency === c ? "border-crimson bg-crimson text-white" : "border-linec text-mutedc"}`}
                    >
                      {c === "INR" ? "₹ INR" : "$ USD"}
                    </button>
                  ))}
                </div>
                {user ? (
                  <Link to="/account" data-testid="header-mobile-account-link" onClick={() => setMenuOpen(false)} className="block py-2.5 font-serif text-2xl text-crimson">
                    My Account
                  </Link>
                ) : (
                  <button data-testid="header-mobile-signin" onClick={() => { setMenuOpen(false); signIn(); }} className="mt-3 flex w-full items-center justify-center gap-2 rounded-full bg-ink py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-crimson">
                    <UserIcon size={15} /> Sign in with Google
                  </button>
                )}
                <div className="flex items-center gap-1 pt-4" data-testid="header-mobile-socials">
                  <a
                    data-testid="header-mobile-instagram-link"
                    href={(config.socials || {}).instagram || "https://instagram.com/justkords"}
                    target="_blank" rel="noopener noreferrer"
                    aria-label="Instagram"
                    className="rounded-full border border-linec p-2.5 text-ink transition-colors duration-200 hover:border-crimson hover:text-crimson"
                  >
                    <Instagram size={17} />
                  </a>
                  <a
                    data-testid="header-mobile-facebook-link"
                    href={(config.socials || {}).facebook || "https://facebook.com/justkords"}
                    target="_blank" rel="noopener noreferrer"
                    aria-label="Facebook"
                    className="rounded-full border border-linec p-2.5 text-ink transition-colors duration-200 hover:border-crimson hover:text-crimson"
                  >
                    <Facebook size={17} />
                  </a>
                  <a
                    data-testid="header-mobile-whatsapp-link"
                    href={(config.socials || {}).whatsapp || "https://wa.me/919960705886"}
                    target="_blank" rel="noopener noreferrer"
                    aria-label="WhatsApp"
                    className="rounded-full border border-linec p-2.5 text-ink transition-colors duration-200 hover:border-crimson hover:text-crimson"
                  >
                    <WhatsAppIcon size={16} />
                  </a>
                </div>
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
}
